import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Link,
  RadioGroup,
  Select,
  SelectProps,
  SpaceBetween,
  TimeInput,
} from "@amzn/awsui-components-console";

import AssociateNotFoundBanner from "src/components/associateNotFoundBanner/AssociateNotFoundBanner";
import LoadingIndicator from "src/components/loadingIndicator/LoadingIndicator";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { ACCESS_REQUEST_URL } from "src/app/constants";
import { CUSTOM_ERRORS, INVALID_ASSOCIATE_HOME_SITE_ERROR } from "src/forms/customErrors";
import {
  DIFFERENT_COMPANY_LOCATION,
  EMPLOYEES_DESIGNATED_COMPANY_LOCATION,
  LOCATION_TYPE_OPTIONS,
} from "src/forms/options";
import { listSites, getSite } from "src/sites/sitesSlice";
import { SiteTypes } from "src/sites/constants";
import {
  getEmployeeDetailsService,
  isAssociateFound,
  isEmployeeNotFoundError,
} from "src/api/employee/getEmployeeDetails";
import { useFeatureFlags } from "src/app/featureFlags";

import { resolveWcTpa, toWcSchemaTpa } from "./wcTpaResolution";
import type { WcPreScreenData } from "./types";

interface WcPreScreenProps {
  /**
   * Invoked when the user submits the WC pre-screen with all required
   * fields populated and a routable TPA. Receives the full
   * `WcPreScreenData` payload that the orchestrator persists to
   * `state.jsonForms.preScreen.wc` and uses to build the WC schema name
   * (`WC-<resolvedTpa>-v1_0_0`).
   */
  onPreScreenSubmit: (data: WcPreScreenData) => void;
}

interface AssociateLookupResult {
  firstName: string;
  lastName: string;
  homeSite: string;
  workerSubType: string;
  supervisorAlias: string;
  supervisorName: string;
}

const RADIO_AT_HOME_SITE_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

type LookupStatus = "idle" | "loading" | "success" | "error";

/**
 * The three lookup error scenarios render different guidance:
 * - "associate-not-found": no associate record at all — verify the
 *   associate / yellow-badge handling (shared banner).
 * - "site-not-found": associate exists but has no home site launched
 *   in Nucleus — SIM / RIVER referral guidance.
 * - "lookup-failed": the lookup call itself threw.
 */
type LookupError =
  | { kind: "associate-not-found" }
  | { kind: "site-not-found" }
  | { kind: "lookup-failed"; message: string };

/**
 * Renders the alert matching the lookup error scenario. The
 * associate-not-found and site-not-found contents are shared with the
 * legacy WC form (via `AssociateNotFoundBanner` / `CUSTOM_ERRORS`).
 */
function LookupErrorAlert({ error }: { error: LookupError }): JSX.Element {
  if (error.kind === "associate-not-found") {
    return <AssociateNotFoundBanner testId="wc-pre-screen-associate-not-found-error" />;
  }
  if (error.kind === "site-not-found") {
    const siteNotFoundError = CUSTOM_ERRORS[INVALID_ASSOCIATE_HOME_SITE_ERROR];
    return (
      <Alert
        type="error"
        header={siteNotFoundError.header}
        statusIconAriaLabel="Error"
        data-testid="wc-pre-screen-site-not-found-error"
      >
        {siteNotFoundError.content}
      </Alert>
    );
  }
  return (
    <Alert type="error" header="Associate lookup failed" data-testid="wc-pre-screen-lookup-error">
      <SpaceBetween size="s" direction="vertical">
        <span>{error.message}</span>
        <Link href={ACCESS_REQUEST_URL} target="_blank" external>
          Request access
        </Link>
      </SpaceBetween>
    </Alert>
  );
}

interface AssociateLookupSectionProps {
  alias: string;
  setAlias: (value: string) => void;
  employeeId: string;
  setEmployeeId: (value: string) => void;
  status: LookupStatus;
  error: LookupError | null;
  result: AssociateLookupResult | null;
  onSearch: () => void;
}

/**
 * Stage-1 sub-form rendered by `WcPreScreen`. Collects the alias /
 * employee ID, surfaces the lookup-status branch (loading / error /
 * read-only-success), and delegates the actual fetch to the parent so
 * the parent owns the captured associate result.
 *
 * Extracted from the parent component to keep `WcPreScreen`'s
 * cyclomatic complexity below the project's lint threshold.
 */
function AssociateLookupSection({
  alias,
  setAlias,
  employeeId,
  setEmployeeId,
  status,
  error,
  result,
  onSearch,
}: AssociateLookupSectionProps): JSX.Element {
  const lookupReady = alias.trim().length > 0 && employeeId.trim().length > 0;
  const inputsDisabled = status === "loading";
  return (
    <Container
      header={
        <Header variant="h2" headingTagOverride="h3">
          Associate
        </Header>
      }
    >
      <SpaceBetween size="m" direction="vertical">
        <FormField label="Alias" data-testid="wc-pre-screen-alias-form-field">
          <Input
            value={alias}
            onChange={({ detail }) => setAlias(detail.value)}
            data-testid="wc-pre-screen-alias-input"
            disabled={inputsDisabled}
          />
        </FormField>
        <FormField label="Employee ID" data-testid="wc-pre-screen-employee-id-form-field">
          <Input
            value={employeeId}
            onChange={({ detail }) => setEmployeeId(detail.value)}
            data-testid="wc-pre-screen-employee-id-input"
            disabled={inputsDisabled}
          />
        </FormField>
        <Button
          onClick={onSearch}
          disabled={!lookupReady || inputsDisabled}
          data-testid="wc-pre-screen-search-associate-button"
        >
          Search Associate
        </Button>
        {status === "loading" ? (
          <LoadingIndicator label="Looking up associate..." testId="wc-pre-screen-lookup-spinner" />
        ) : null}
        {status === "error" && error ? <LookupErrorAlert error={error} /> : null}
        {status === "success" && result ? (
          <SpaceBetween size="s" direction="vertical">
            <FormField label="First name">
              <Input
                value={result.firstName}
                readOnly
                disabled
                data-testid="wc-pre-screen-first-name-input"
              />
            </FormField>
            <FormField label="Last name">
              <Input
                value={result.lastName}
                readOnly
                disabled
                data-testid="wc-pre-screen-last-name-input"
              />
            </FormField>
            <FormField label="Home site">
              <Input
                value={result.homeSite}
                readOnly
                disabled
                data-testid="wc-pre-screen-home-site-input"
              />
            </FormField>
          </SpaceBetween>
        ) : null}
      </SpaceBetween>
    </Container>
  );
}

interface IncidentDetailsSectionProps {
  incidentAtHomeSite: string | null;
  onIncidentAtHomeSiteChange: (value: string) => void;
  locationType: SelectProps.Option | null;
  onLocationTypeChange: (option: SelectProps.Option | null) => void;
  incidentSiteOption: SelectProps.Option | null;
  onIncidentSiteChange: (option: SelectProps.Option | null) => void;
  sitesOptions: SelectProps.Option[];
  incidentDate: string;
  setIncidentDate: (value: string) => void;
  incidentTime: string;
  setIncidentTime: (value: string) => void;
}

/**
 * Stage-2 sub-form rendered by `WcPreScreen` once the associate lookup
 * has succeeded. Owns the conditional visibility of `locationType` and
 * `incidentSiteCode` and surfaces all input changes back to the parent.
 */
function IncidentDetailsSection({
  incidentAtHomeSite,
  onIncidentAtHomeSiteChange,
  locationType,
  onLocationTypeChange,
  incidentSiteOption,
  onIncidentSiteChange,
  sitesOptions,
  incidentDate,
  setIncidentDate,
  incidentTime,
  setIncidentTime,
}: IncidentDetailsSectionProps): JSX.Element {
  const showLocationType = incidentAtHomeSite === "false";
  const showIncidentSiteSelect =
    showLocationType && locationType?.value === DIFFERENT_COMPANY_LOCATION.value;
  return (
    <Container
      header={
        <Header variant="h2" headingTagOverride="h3">
          Incident details
        </Header>
      }
    >
      <SpaceBetween size="m" direction="vertical">
        <FormField
          label="Did the injury/illness occur at the associate's home site?"
          description="Home site is where the associate normally works."
          data-testid="wc-pre-screen-at-home-site-form-field"
        >
          <RadioGroup
            value={incidentAtHomeSite ?? ""}
            items={RADIO_AT_HOME_SITE_OPTIONS}
            onChange={({ detail }) => onIncidentAtHomeSiteChange(detail.value)}
            data-testid="wc-pre-screen-at-home-site-radio"
          />
        </FormField>
        {showLocationType ? (
          <FormField
            label="What type of location did the incident occur at?"
            data-testid="wc-pre-screen-location-type-form-field"
          >
            <Select
              options={LOCATION_TYPE_OPTIONS}
              selectedOption={locationType}
              onChange={({ detail }) => onLocationTypeChange(detail.selectedOption)}
              placeholder="Please select the type of location of the incident/injury"
              data-testid="wc-pre-screen-location-type-select"
            />
          </FormField>
        ) : null}
        {showIncidentSiteSelect ? (
          <FormField
            label="Please select the site where the injury occurred"
            data-testid="wc-pre-screen-incident-site-form-field"
          >
            <Select
              options={sitesOptions}
              selectedOption={incidentSiteOption}
              onChange={({ detail }) => onIncidentSiteChange(detail.selectedOption)}
              placeholder="Please select the incident site"
              data-testid="wc-pre-screen-incident-site-select"
            />
          </FormField>
        ) : null}
        <FormField
          label="Incident Date"
          description="Input Date in YYYY/MM/DD format"
          data-testid="wc-pre-screen-incident-date-form-field"
        >
          <DatePicker
            value={incidentDate}
            onChange={({ detail }) => setIncidentDate(detail.value)}
            placeholder="YYYY/MM/DD"
            data-testid="wc-pre-screen-incident-date-input"
          />
        </FormField>
        <FormField
          label="Incident Time"
          description="Input time in 24h format (hh:mm)"
          data-testid="wc-pre-screen-incident-time-form-field"
        >
          <TimeInput
            value={incidentTime}
            onChange={({ detail }) => setIncidentTime(detail.value)}
            format="hh:mm"
            placeholder="hh:mm"
            data-testid="wc-pre-screen-incident-time-input"
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}

function buildWcPreScreenPayload(args: {
  alias: string;
  employeeId: string;
  associate: AssociateLookupResult;
  incidentAtHome: boolean;
  locationType: SelectProps.Option | null;
  incidentSiteOption: SelectProps.Option | null;
  incidentCountry: string | null;
  incidentState: string | null;
  incidentDate: string;
  incidentTime: string;
  resolvedTpa: WcPreScreenData["resolvedTpa"];
}): WcPreScreenData {
  const {
    alias,
    employeeId,
    associate,
    incidentAtHome,
    locationType,
    incidentSiteOption,
    incidentCountry,
    incidentState,
    incidentDate,
    incidentTime,
    resolvedTpa,
  } = args;
  return {
    associateEmployeeId: employeeId.trim(),
    associateAlias: alias.trim(),
    associateFirstName: associate.firstName,
    associateLastName: associate.lastName,
    associateHomeSiteCode: associate.homeSite,
    workerSubType: associate.workerSubType,
    supervisorAlias: associate.supervisorAlias,
    supervisorName: associate.supervisorName,
    incidentAtHomeSite: incidentAtHome,
    locationType: incidentAtHome
      ? EMPLOYEES_DESIGNATED_COMPANY_LOCATION.value
      : (locationType?.value ?? null),
    incidentSiteCode: incidentAtHome ? associate.homeSite : (incidentSiteOption?.value ?? null),
    incidentCountry,
    incidentState,
    incidentDate,
    incidentTime,
    resolvedTpa,
  };
}

/**
 * WC pre-screen rendered by `JsonFormsClaimIntakeApp` for the `wc` LOB.
 *
 * Captures the minimum data required to resolve a TPA and build the
 * `WC-<TPA>-v1_0_0` schema name. The flow runs in three stages:
 *
 * 1. Associate lookup (`AssociateLookupSection`) — alias + employee ID,
 *    direct call to `getEmployeeDetailsService`, populates a read-only
 *    block on success.
 * 2. Incident time and place (`IncidentDetailsSection`) — radio for
 *    "incident at home site?", conditional location-type and
 *    incident-site selects, date and time inputs.
 * 3. Submit — runs `resolveWcTpa` + `toWcSchemaTpa` against the
 *    current `state.sites.incidentSite` plus the captured incident
 *    date. If the resolver returns null or the schema mapper rejects
 *    the TPA, an "unsupported TPA" alert appears and
 *    `onPreScreenSubmit` is NOT invoked.
 */
function WcPreScreen({ onPreScreenSubmit }: WcPreScreenProps): JSX.Element {
  const dispatch = useAppDispatch();
  const incidentSite = useAppSelector((state) => state.sites?.incidentSite);
  const sitesList = useAppSelector((state) => state.sites?.sitesList);
  const { isLoading: featureFlagsLoading } = useFeatureFlags();

  const [associateAlias, setAssociateAlias] = useState("");
  const [associateEmployeeId, setAssociateEmployeeId] = useState("");
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>("idle");
  const [lookupError, setLookupError] = useState<LookupError | null>(null);
  const [associateResult, setAssociateResult] = useState<AssociateLookupResult | null>(null);

  const [incidentAtHomeSite, setIncidentAtHomeSite] = useState<string | null>(null);
  const [locationType, setLocationType] = useState<SelectProps.Option | null>(null);
  const [incidentSiteOption, setIncidentSiteOption] = useState<SelectProps.Option | null>(null);
  const [incidentDate, setIncidentDate] = useState("");
  const [incidentTime, setIncidentTime] = useState("");

  // Tracks whether the user has tried to submit but the resolver
  // returned an unsupported TPA. Cleared whenever the user edits any
  // incident input so the alert does not stick around stale.
  const [unsupportedTpa, setUnsupportedTpa] = useState(false);

  const handleAssociateLookup = useCallback(async () => {
    if (associateAlias.trim().length === 0 || associateEmployeeId.trim().length === 0) {
      return;
    }
    setLookupStatus("loading");
    setLookupError(null);
    try {
      const employee = await getEmployeeDetailsService({
        alias: associateAlias.trim(),
        employeeId: associateEmployeeId.trim(),
      });
      if (!isAssociateFound(employee)) {
        setLookupStatus("error");
        setLookupError({ kind: "associate-not-found" });
        return;
      }
      if (!employee.homeSite) {
        setLookupStatus("error");
        setLookupError({ kind: "site-not-found" });
        return;
      }
      setAssociateResult({
        firstName: employee.firstName ?? "",
        lastName: employee.lastName ?? "",
        homeSite: employee.homeSite,
        workerSubType: employee.workerSubType ?? "",
        supervisorAlias: employee.supervisorAlias ?? "",
        supervisorName: employee.supervisorName ?? "",
      });
      // Populate the home site so the resolver can read its
      // country / state when the user selects "incident at home site".
      dispatch(
        getSite({
          code: employee.homeSite,
          type: SiteTypes.ASSOCIATE_HOME_SITE,
        })
      );
      setLookupStatus("success");
    } catch (err) {
      setLookupStatus("error");
      // The service rejects with E1900 when the employee ID has no
      // record — that is the associate-not-found scenario, not a
      // generic lookup failure.
      if (isEmployeeNotFoundError(err)) {
        setLookupError({ kind: "associate-not-found" });
        return;
      }
      setLookupError({
        kind: "lookup-failed",
        message:
          err instanceof Error
            ? `Associate lookup failed: ${err.message}`
            : "Associate lookup failed. Please try again.",
      });
    }
  }, [associateAlias, associateEmployeeId, dispatch]);

  // When the user selects "Yes" (incident at home site), align the
  // sites slice so `incidentSite` returns the home site and the
  // resolver can read its country / state.
  useEffect(() => {
    if (incidentAtHomeSite === "true") {
      dispatch({ type: "sites/setIncidentSiteAsHomeSite" });
    }
  }, [incidentAtHomeSite, dispatch]);

  const handleIncidentAtHomeSiteChange = useCallback((value: string) => {
    setIncidentAtHomeSite(value);
    setUnsupportedTpa(false);
    if (value === "true") {
      setLocationType(null);
      setIncidentSiteOption(null);
    }
  }, []);

  const handleLocationTypeChange = useCallback(
    (option: SelectProps.Option | null) => {
      setLocationType(option);
      setIncidentSiteOption(null);
      setUnsupportedTpa(false);
      if (option?.value === DIFFERENT_COMPANY_LOCATION.value) {
        dispatch(listSites({ bypassCache: false }));
      }
    },
    [dispatch]
  );

  const handleIncidentSiteChange = useCallback(
    (option: SelectProps.Option | null) => {
      setIncidentSiteOption(option);
      setUnsupportedTpa(false);
      if (option?.value) {
        dispatch(getSite({ code: option.value, type: SiteTypes.INCIDENT_SITE }));
      }
    },
    [dispatch]
  );

  const handleIncidentDateChange = useCallback((value: string) => {
    setIncidentDate(value);
    setUnsupportedTpa(false);
  }, []);

  const handleIncidentTimeChange = useCallback((value: string) => {
    setIncidentTime(value);
    setUnsupportedTpa(false);
  }, []);

  const sitesOptions = useMemo<SelectProps.Option[]>(() => sitesList ?? [], [sitesList]);

  const incidentInputsReady =
    associateResult !== null &&
    incidentAtHomeSite !== null &&
    incidentDate.length > 0 &&
    incidentTime.length > 0 &&
    (incidentAtHomeSite === "true" ||
      (locationType !== null &&
        (locationType.value !== DIFFERENT_COMPANY_LOCATION.value || incidentSiteOption !== null)));

  const handleSubmit = useCallback(() => {
    if (!associateResult || !incidentInputsReady) return;
    const tpa = resolveWcTpa({
      country: incidentSite?.country,
      state: incidentSite?.state,
      incidentDate,
    });
    const schemaTpa = toWcSchemaTpa(tpa);
    if (!schemaTpa) {
      setUnsupportedTpa(true);
      return;
    }
    setUnsupportedTpa(false);
    onPreScreenSubmit(
      buildWcPreScreenPayload({
        alias: associateAlias,
        employeeId: associateEmployeeId,
        associate: associateResult,
        incidentAtHome: incidentAtHomeSite === "true",
        locationType,
        incidentSiteOption,
        incidentCountry: incidentSite?.country ?? null,
        incidentState: incidentSite?.state ?? null,
        incidentDate,
        incidentTime,
        resolvedTpa: schemaTpa,
      })
    );
  }, [
    associateResult,
    incidentInputsReady,
    incidentSite?.country,
    incidentSite?.state,
    incidentDate,
    incidentTime,
    incidentAtHomeSite,
    locationType,
    incidentSiteOption,
    associateEmployeeId,
    associateAlias,
    onPreScreenSubmit,
  ]);

  return (
    <Box padding="l" data-testid="wc-pre-screen">
      <SpaceBetween size="l" direction="vertical">
        <Header variant="h2">Workers Compensation claim intake</Header>
        <AssociateLookupSection
          alias={associateAlias}
          setAlias={setAssociateAlias}
          employeeId={associateEmployeeId}
          setEmployeeId={setAssociateEmployeeId}
          status={lookupStatus}
          error={lookupError}
          result={associateResult}
          onSearch={handleAssociateLookup}
        />
        {lookupStatus === "success" && associateResult ? (
          <IncidentDetailsSection
            incidentAtHomeSite={incidentAtHomeSite}
            onIncidentAtHomeSiteChange={handleIncidentAtHomeSiteChange}
            locationType={locationType}
            onLocationTypeChange={handleLocationTypeChange}
            incidentSiteOption={incidentSiteOption}
            onIncidentSiteChange={handleIncidentSiteChange}
            sitesOptions={sitesOptions}
            incidentDate={incidentDate}
            setIncidentDate={handleIncidentDateChange}
            incidentTime={incidentTime}
            setIncidentTime={handleIncidentTimeChange}
          />
        ) : null}
        {unsupportedTpa ? (
          <Alert
            type="warning"
            header="Unsupported TPA"
            data-testid="wc-pre-screen-unsupported-tpa-alert"
          >
            We couldn&apos;t route this incident to a supported Third Party Administrator. Please
            verify the incident site and date, or open an access ticket if you believe your country
            / state should be supported.
          </Alert>
        ) : null}
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!incidentInputsReady || featureFlagsLoading}
          data-testid="wc-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>
    </Box>
  );
}

export default WcPreScreen;
